import { useMemo, useState } from "react";

type Mcq = {
  id: string;
  topic_id: string | null;
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string | null;
  difficulty: number;
  tags: string[] | null;
  review_status?: 'pending' | 'approved' | 'needs_revision';
  last_reviewed_at?: string | null;
  exam_labels?: Array<{
    id: string;
    name: string;
    slug: string;
    color: string;
  }>;
};

type QuickFilter = 'all' | 'pending' | 'needs_revision' | 'missing_explanation' | 'recent' | 'high_difficulty';

export const useMCQFilters = (mcqs: Mcq[]) => {
  const [query, setQuery] = useState("");
  const [topicFilter, setTopicFilter] = useState<string | undefined>();
  const [difficultyFilter, setDifficultyFilter] = useState<string | undefined>();
  const [reviewStatusFilter, setReviewStatusFilter] = useState<string | undefined>();
  const [examLabelFilter, setExamLabelFilter] = useState<string[]>([]);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return mcqs.filter((m) => {
      // Text search
      const matchesQuery = (m.question || '').toLowerCase().includes(q);
      
      // Standard filters
      const matchesTopic = !topicFilter || m.topic_id === topicFilter;
      const matchesDiff = !difficultyFilter || String(m.difficulty) === difficultyFilter;
      const matchesReviewStatus = !reviewStatusFilter || m.review_status === reviewStatusFilter;
      const matchesExamLabel = examLabelFilter.length === 0 || 
        (m.exam_labels && m.exam_labels.some(label => examLabelFilter.includes(label.id)));

      // Quick filters
      let matchesQuickFilter = true;
      switch (quickFilter) {
        case 'all':
          matchesQuickFilter = true;
          break;
        case 'pending':
          matchesQuickFilter = m.review_status === 'pending';
          break;
        case 'needs_revision':
          matchesQuickFilter = m.review_status === 'needs_revision';
          break;
        case 'missing_explanation':
          matchesQuickFilter = !m.explanation || m.explanation.trim() === '';
          break;
        case 'recent':
          matchesQuickFilter = m.last_reviewed_at 
            ? new Date(m.last_reviewed_at) >= sevenDaysAgo 
            : false;
          break;
        case 'high_difficulty':
          matchesQuickFilter = m.difficulty >= 4;
          break;
      }

      return matchesQuery && matchesTopic && matchesDiff && matchesReviewStatus && matchesExamLabel && matchesQuickFilter;
    });
  }, [mcqs, query, topicFilter, difficultyFilter, reviewStatusFilter, examLabelFilter, quickFilter]);

  const resetFilters = () => {
    setQuery("");
    setTopicFilter(undefined);
    setDifficultyFilter(undefined);
    setReviewStatusFilter(undefined);
    setExamLabelFilter([]);
    setQuickFilter('all');
  };

  return {
    filtered,
    query,
    setQuery,
    topicFilter,
    setTopicFilter,
    difficultyFilter,
    setDifficultyFilter,
    reviewStatusFilter,
    setReviewStatusFilter,
    examLabelFilter,
    setExamLabelFilter,
    quickFilter,
    setQuickFilter,
    resetFilters,
  };
};
